"use client";

import {useEffect,useState} from "react";
import {useRouter} from "next/navigation";
import Link from "next/link";
import {ArrowRight,ArrowUpRight,Code2,KeyRound,LockKeyhole,ShieldCheck} from "lucide-react";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {requestCore,ApiError} from "@/lib/developer-api";

function nextPath(){
  const p=new URLSearchParams(window.location.search).get("next");
  return p?.startsWith("/")&&!p.startsWith("//")&&!p.startsWith("/\\")?p:"/";
}
export default function Login(){
  const router=useRouter();
  const [email,setEmail]=useState("");
  const [password,setPassword]=useState("");
  const [busy,setBusy]=useState(false);
  const [checking,setChecking]=useState(true);
  const [error,setError]=useState("");
  const [mfaRequired,setMfaRequired]=useState(false);
  const [mfaMethod,setMfaMethod]=useState<"totp"|"recovery">("totp");
  const [mfaCode,setMfaCode]=useState("");
  useEffect(()=>{
    let active=true;
    requestCore("users/me").then(()=>{
      if(active)router.replace(nextPath());
    }).catch(()=>{if(active)setChecking(false);});
    return ()=>{active=false;};
  },[router]);

  async function submit(event:React.FormEvent){
    event.preventDefault();
    setBusy(true);setError("");
    try{
      const payload:{email:string;password:string;mfaCode?:string;recoveryCode?:string}={email,password};
      if(mfaRequired){
        if(mfaMethod==="totp")payload.mfaCode=mfaCode.trim();
        else payload.recoveryCode=mfaCode.trim();
      }
      await requestCore("auth/login",{method:"POST",body:JSON.stringify(payload)},false);
      window.location.assign(nextPath());
    }catch(e){
      if(e instanceof ApiError && e.code==="ADMIN_MFA_REQUIRED"){
        setMfaRequired(true);
        setError("관리자 계정의 추가 인증이 필요합니다. 인증 앱 코드 또는 복구 코드를 입력하세요.");
      }else{
        setError(e instanceof Error?e.message:"로그인할 수 없습니다.");
      }
      setBusy(false);
    }
  }
  const signedOut=typeof window!=="undefined"&&new URLSearchParams(window.location.search).has("signed_out");
  return <main className="grid min-h-screen lg:grid-cols-[minmax(400px,47%)_1fr]">
    <section className="relative hidden min-h-screen flex-col justify-between overflow-hidden bg-[#101213] px-14 py-12 text-white lg:flex">
      <Link href="/login" className="flex w-fit items-center gap-3 font-semibold text-lg tracking-tight">
        <span className="flex size-9 items-center justify-center rounded-[11px] border border-white/20"><Code2 className="size-[19px]"/></span>
        SEEXR <span className="font-normal text-white/55">Developers</span>
      </Link>
      <div className="relative z-10 max-w-[570px]">
        <div className="mb-9 inline-flex items-center gap-2 rounded-full border border-white/20 px-3.5 py-1.5 text-xs text-white/70">
          <span className="size-1.5 rounded-full bg-[#b4f2c9]"/> Developer Platform
        </div>
        <h1 className="text-[clamp(42px,4.7vw,72px)] leading-[1.075] font-medium tracking-[-.06em]">
          Build with<br/>SEEXR.
        </h1>
        <p className="mt-8 max-w-[390px] text-[16px] leading-8 text-white/55">건설 안전 데이터와 AI 서비스를 연결하는 단일 개발자 워크스페이스.</p>
        <div className="mt-14 grid max-w-md grid-cols-2 gap-8 border-t border-white/15 pt-7 text-sm">
          <div><KeyRound className="mb-4 size-5 text-white/70"/><p className="font-medium">One key, one workspace</p><p className="mt-2 text-xs leading-6 text-white/50">SEEXR 계정 기반의 API 키 관리</p></div>
          <div><ShieldCheck className="mb-4 size-5 text-white/70"/><p className="font-medium">Core authentication</p><p className="mt-2 text-xs leading-6 text-white/50">기존 SEEXR 보안 세션 연동</p></div>
        </div>
      </div>
      <p className="relative z-10 text-xs text-white/35">© 2026 SEEXR. Developer tools.</p>
      <div aria-hidden className="pointer-events-none absolute -right-32 -bottom-40 size-[600px] rounded-full border border-white/8"/>
      <div aria-hidden className="pointer-events-none absolute -right-18 -bottom-24 size-[420px] rounded-full border border-white/8"/>
    </section>

    <section className="flex min-h-screen flex-col bg-[var(--page)] text-[var(--ink)] px-6 py-8 sm:px-12">
      <div className="flex items-center justify-between lg:justify-end">
        <div className="flex items-center gap-2 font-semibold tracking-tight lg:hidden"><Code2 className="size-5"/> SEEXR Developers</div>
        <a href={process.env.NEXT_PUBLIC_APP_URL||"https://app.seexr.dev"} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-[13px] text-[var(--muted-ink)] transition hover:text-[var(--ink)]">SEEXR 서비스 <ArrowUpRight className="size-4"/></a>
      </div>
      <div className="mx-auto flex w-full max-w-[390px] flex-1 flex-col justify-center py-20">
        <div className="mb-10">
          <h2 className="text-[34px] font-semibold tracking-[-.055em] text-[var(--ink)]">다시 오신 것을 환영합니다</h2>
          <p className="mt-3 text-[14px] leading-6 text-[var(--muted-ink)]">SEEXR 계정으로 개발자 플랫폼에 로그인하세요.</p>
        </div>
        {signedOut&&<p role="status" className="mb-5 rounded-lg border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-950/40 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-300">안전하게 로그아웃했습니다.</p>}
        <form className="space-y-5" onSubmit={e=>void submit(e)}>
          {!mfaRequired?<>
            <div className="space-y-2"><Label htmlFor="email" className="text-[13px] font-medium">이메일 주소</Label><Input id="email" type="email" autoComplete="username" required autoFocus placeholder="name@company.com" className="h-11 rounded-[9px] border-[var(--stroke)] bg-[var(--surface)] text-[var(--ink)] px-3.5 text-[14px]" value={email} onChange={e=>setEmail(e.target.value)}/></div>
            <div className="space-y-2"><Label htmlFor="password" className="text-[13px] font-medium">비밀번호</Label><Input id="password" type="password" autoComplete="current-password" required className="h-11 rounded-[9px] border-[var(--stroke)] bg-[var(--surface)] text-[var(--ink)] px-3.5 text-[14px]" value={password} onChange={e=>setPassword(e.target.value)}/></div>
          </>:<>
            <p className="text-[13px] leading-6 text-[var(--muted-ink)]">{email} 계정의 2단계 인증</p>
            <div className="space-y-2"><Label htmlFor="mfaCode" className="text-[13px] font-medium">{mfaMethod==="totp"?"인증 앱의 6자리 코드":"관리자 복구 코드"}</Label>
              <Input id="mfaCode" autoFocus autoComplete={mfaMethod==="totp"?"one-time-code":"off"} type="text" required inputMode={mfaMethod==="totp"?"numeric":"text"} maxLength={mfaMethod==="totp"?6:100} pattern={mfaMethod==="totp"?"[0-9]{6}":undefined} placeholder={mfaMethod==="totp"?"000000":"복구 코드 입력"} className="h-11 rounded-[9px] border-[var(--stroke)] bg-[var(--surface)] text-[var(--ink)] px-3.5 text-[14px]" value={mfaCode} onChange={e=>setMfaCode(e.target.value)}/>
            </div>
            <div className="flex items-center justify-between text-[12px]"><button type="button" className="text-[var(--muted-ink)] underline underline-offset-4 hover:text-[var(--ink)]" onClick={()=>{setMfaCode("");setError("");setMfaMethod(method=>method==="totp"?"recovery":"totp");}}>{mfaMethod==="totp"?"복구 코드로 인증":"인증 앱 코드로 인증"}</button>
              <button type="button" className="text-[var(--muted-ink)] hover:text-[var(--ink)]" onClick={()=>{setMfaRequired(false);setMfaCode("");setError("");}}>계정 변경</button>
            </div>
          </>}
          {error&&<p role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-[13px] text-red-700 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-200">{error}</p>}
          <Button type="submit" disabled={busy||checking||(mfaRequired&&!mfaCode.trim())} className="mt-2 h-11 w-full rounded-[9px] bg-[var(--solid-button)] text-[var(--solid-ink)] text-[14px] font-medium hover:opacity-85">{busy?"로그인 중…":checking?"세션 확인 중…":<>{mfaRequired?"인증하기":"계속하기"} <ArrowRight className="ml-1 size-4"/></>}</Button>
        </form>
        <div className="my-8 flex items-center gap-3 text-xs text-[var(--muted-ink)]"><div className="h-px flex-1 bg-[var(--stroke)]"/>SEEXR 계정 통합 인증<div className="h-px flex-1 bg-[var(--stroke)]"/></div>
        <p className="text-center text-[13px] text-[var(--muted-ink)]">계정이 없으신가요? <a className="font-semibold text-[var(--ink)] underline decoration-[var(--stroke)] underline-offset-4 hover:decoration-[var(--ink)]" href={(process.env.NEXT_PUBLIC_APP_URL||"https://app.seexr.dev")+"/signup"}>회원가입</a></p>
        <div className="mt-12 flex items-start gap-2.5 border-t border-[var(--stroke)] pt-6 text-[12px] leading-5 text-[var(--muted-ink)]"><LockKeyhole className="mt-0.5 size-4 shrink-0"/>본 포털은 별도 계정을 만들지 않고 SEEXR Core 세션을 검증합니다. 기존 서비스에 로그인되어 있으면 자동으로 이어집니다.</div>
      </div>
      <div className="text-center text-[12px] text-[var(--muted-ink)]">SEEXR Developer Platform</div>
    </section>
  </main>;
}